import { Reveal } from "@/components/Reveal";
import { Brain, Cpu, Boxes, GitBranch, ShieldCheck, Workflow } from "lucide-react";

const services = [
  {
    icon: Brain,
    title: "GenAI & Agentic Consulting",
    desc: "Strategy, readiness assessments, and pragmatic roadmaps that turn AI ambition into a shipping plan.",
    tags: ["Strategy", "Readiness Audit", "Roadmapping"],
    span: "lg:col-span-3",
  },
  {
    icon: Cpu,
    title: "AI Engineering",
    desc: "We build autonomous agents, RAG pipelines, and LLM applications engineered for production scale.",
    tags: ["Agents", "RAG", "LLM Apps"],
    span: "lg:col-span-3",
  },
  {
    icon: Workflow,
    title: "Multi-Agent Orchestration",
    desc: "Coordinated agent fleets that plan, act, and self-correct across complex enterprise workflows.",
    tags: ["Planning", "Tool-use"],
    span: "lg:col-span-2",
  },
  {
    icon: GitBranch,
    title: "MLOps & Evaluation",
    desc: "Observability, evals, and CI/CD so your models stay accurate, safe, and continuously improving.",
    tags: ["Evals", "CI/CD"],
    span: "lg:col-span-2",
  },
  {
    icon: ShieldCheck,
    title: "Governance & Safety",
    desc: "Guardrails, red-teaming, and compliance baked in from day one — not bolted on later.",
    tags: ["Guardrails", "Compliance"],
    span: "lg:col-span-2",
  },
];

export default function Services() {
  return (
    <section id="services" className="relative bg-[#050505] py-24 lg:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-gradient-to-r from-[#4f6bff] to-[#9d6bff]" />
              <span className="overline text-[#8b7cff]/90">01 — Capabilities</span>
            </div>
            <h2 className="font-display font-700 tracking-tighter leading-none text-white text-4xl sm:text-5xl max-w-2xl">
              Consulting and engineering, under one roof.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-white/55 max-w-sm leading-relaxed">
              From the first whiteboard session to a live, monitored deployment — a single
              team that owns the outcome end to end.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal
                key={s.title}
                delay={i * 0.06}
                className={`group ${s.span}`}
              >
                <div
                  className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-8 hover:border-white/25 hover:bg-white/[0.04] hover:-translate-y-1 transition-[transform,border-color,background-color] duration-500"
                  data-testid={`service-card-${i}`}
                >
                  <div className="h-12 w-12 rounded-xl border border-white/10 flex items-center justify-center mb-7 group-hover:border-[#8b7cff]/40 transition-colors duration-500">
                    <Icon className="h-5 w-5 text-[#8b7cff]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-xl font-500 text-white tracking-tight mb-3">{s.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed mb-6">{s.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {s.tags.map((t) => (
                      <span key={t} className="text-xs text-white/50 border border-white/10 rounded-full px-3 py-1">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            );
          })}
          <Reveal delay={0.3} className="lg:col-span-6">
            <div className="rounded-2xl border border-white/10 bg-gradient-to-r from-white/[0.04] to-transparent p-8 flex items-center gap-4">
              <Boxes className="h-6 w-6 text-[#8b7cff] shrink-0" strokeWidth={1.5} />
              <p className="text-white/70 text-sm sm:text-base">
                Not sure where to start? Our <span className="text-white font-500">2-week AI Sprint</span> turns
                a fuzzy idea into a working prototype and a costed rollout plan.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
